import { Editor, Transforms, Text, Range } from 'vendor/slate'
import { v4 as uuidv4 } from 'uuid'
import { getSuggestionKey, SUGGESTION_ROOT, SUGGESTION_TYPES } from './constants'

const buildSuggestionProps = (id, type, userId) => ({
  [SUGGESTION_ROOT]: true,
  [getSuggestionKey(id)]: {
    id,
    type,
    userId,
    createdAt: Date.now()
  }
})

export const getSuggestionIdFromLeaf = leaf => {
  if (!leaf || !leaf[SUGGESTION_ROOT]) return null

  const keys = Object.keys(leaf).filter(key => key.startsWith('suggestion_') && key !== 'suggestion_transient')

  if (keys.length === 0) return null

  const data = leaf[keys[keys.length - 1]]
  return data?.id ?? null
}

export const getSuggestionTypeFromLeaf = (leaf, suggestionId) => {
  const data = leaf?.[getSuggestionKey(suggestionId)]
  return data?.type ?? null
}

export const findSuggestionIds = editor => {
  const ids = new Set()

  for (const [node] of Editor.nodes(editor, {
    at: [],
    match: n => Text.isText(n) && n[SUGGESTION_ROOT]
  })) {
    const id = getSuggestionIdFromLeaf(node)
    if (id) ids.add(id)
  }

  return [...ids]
}

const nodesForSuggestion = (editor, suggestionId, type) =>
  [...Editor.nodes(editor, {
    at: [],
    match: n => Text.isText(n) && n[getSuggestionKey(suggestionId)]?.type === type
  })]

export const acceptSuggestionById = (editor, suggestionId) => {
  if (!suggestionId) return

  Editor.withoutNormalizing(editor, () => {
    const removeEntries = nodesForSuggestion(editor, suggestionId, SUGGESTION_TYPES.remove).reverse()

    removeEntries.forEach(([, path]) => {
      Transforms.removeNodes(editor, { at: path })
    })

    const insertEntries = nodesForSuggestion(editor, suggestionId, SUGGESTION_TYPES.insert)

    insertEntries.forEach(([, path]) => {
      Transforms.unsetNodes(editor, [SUGGESTION_ROOT, getSuggestionKey(suggestionId)], { at: path })
    })
  })

  if (editor.activeSuggestionId === suggestionId) {
    editor.activeSuggestionId = null
  }
}

export const rejectSuggestionById = (editor, suggestionId) => {
  if (!suggestionId) return

  Editor.withoutNormalizing(editor, () => {
    const insertEntries = nodesForSuggestion(editor, suggestionId, SUGGESTION_TYPES.insert).reverse()

    insertEntries.forEach(([, path]) => {
      Transforms.removeNodes(editor, { at: path })
    })

    const removeEntries = nodesForSuggestion(editor, suggestionId, SUGGESTION_TYPES.remove)

    removeEntries.forEach(([, path]) => {
      Transforms.unsetNodes(editor, [SUGGESTION_ROOT, getSuggestionKey(suggestionId)], { at: path })
    })
  })

  if (editor.activeSuggestionId === suggestionId) {
    editor.activeSuggestionId = null
  }
}

export const markRangeAsRemove = (editor, range, suggestionId, userId) => {
  if (!range || Range.isCollapsed(range)) return

  Transforms.setNodes(editor, buildSuggestionProps(suggestionId, SUGGESTION_TYPES.remove, userId), {
    at: range,
    match: Text.isText,
    split: true
  })
}

export const insertTextAsSuggestion = (editor, text, suggestionId, userId) => {
  if (!text) return

  Transforms.insertNodes(
    editor,
    {
      text,
      ...buildSuggestionProps(suggestionId, SUGGESTION_TYPES.insert, userId)
    },
    { select: true }
  )

  // Collapse to the end of the inserted node so the next keystroke appends
  // rather than treating the freshly-inserted text as an expanded selection.
  Transforms.collapse(editor, { edge: 'end' })
}

export const isRemovedSuggestionLeaf = leaf => {
  if (!leaf || !leaf[SUGGESTION_ROOT]) return false

  return Object.keys(leaf).some(
    key => key.startsWith('suggestion_') && key !== 'suggestion_transient' && leaf[key]?.type === SUGGESTION_TYPES.remove
  )
}

/** Plate-style AI edit: strikethrough old range, insert proposed text as suggestions. */
export const applyAIEditSuggestion = (editor, { range, newText, userId, suggestionId = uuidv4() }) => {
  if (!range || !newText?.trim()) return null

  Editor.withoutNormalizing(editor, () => {
    markRangeAsRemove(editor, range, suggestionId, userId)

    const end = Editor.end(editor, range)
    Transforms.select(editor, end)
    insertTextAsSuggestion(editor, newText, suggestionId, userId)
  })

  editor.activeSuggestionId = suggestionId
  return suggestionId
}

/** Replace selection with AI-generated text (no track-changes marks). */
export const applyAIReplaceSelection = (editor, { range, newText }) => {
  if (!range) return

  Editor.withoutNormalizing(editor, () => {
    Transforms.select(editor, range)
    Transforms.delete(editor, { at: range })
    Transforms.insertText(editor, newText)
  })
}

export const acceptAllSuggestions = editor => {
  findSuggestionIds(editor).forEach(id => acceptSuggestionById(editor, id))
}

export const rejectAllSuggestions = editor => {
  findSuggestionIds(editor).forEach(id => rejectSuggestionById(editor, id))
}
